import { Component, OnDestroy, OnInit, AfterViewInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import { TicketRequest } from 'src/app/models/ticketOrder';
import { BenefitsService } from '../../../services/benefits.service';
import { AuthService } from 'src/app/services/auth.service';
import { TicketService } from 'src/app/services/ticket.service';
import { SoldeService } from 'src/app/services/solde.service';
import { BonsService } from 'src/app/services/bons.service';
import { forkJoin, Subscription, map, Observable, catchError, of } from 'rxjs';
import { ModalField } from 'src/app/modal/modal.component';

declare var initFlowbite: () => void;
declare interface ModalInterface {
  show(): void;
  hide(): void;
}
declare class Modal {
  constructor(element: HTMLElement | null, options?: any);
  show(): void;
  hide(): void;
}

interface MerchantVoucher {
  tokenId: string;
  label: string;
  balance: number; // valeur totale détenue pour ce tokenId (quantité × faciale), en XOF
}

@Component({
  selector: 'app-header-marchand',
  templateUrl: './header-marchand.component.html',
  styleUrls: ['./header-marchand.component.css'],
  providers: [MessageService],
})
export class HeaderMarchandComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  modalId = 'request-compensation-modal';
  // Valeur sentinelle pour l'option "Bon marchand" dans le menu déroulant —
  // ne correspond à aucun ticketId réel, juste un déclencheur d'affichage.
  readonly BON_MARCHAND_OPTION = 'BON_MARCHAND';
  merchantWalletAddress: string = '';
  acceptedTicketsIds: number[] = [];
  acceptedTickets: any[] = []; // titres B2B/B2C avec un solde > 0, disponibles dans le menu déroulant
  currentBalance: number = 0; // solde du titre B2B/B2C actuellement sélectionné

  // Bons cadeaux marchands — affichés à part, en cases à cocher, jamais dans
  // le menu déroulant : un bon ne se fractionne pas, cocher une ligne
  // demande TOUJOURS sa valeur totale, jamais une saisie libre.
  merchantVouchers: MerchantVoucher[] = [];
  selectedVoucherTokenIds: Set<string> = new Set();

  newRequestCompensationFields: ModalField[] = [];

  private ticketChangesSubscription: Subscription | undefined;
  private balanceSubscription: Subscription | undefined;

  constructor(
    private messageService: MessageService,
    private benefitsService: BenefitsService,
    private authService: AuthService,
    private ticketService: TicketService,
    private soldeService: SoldeService,
    private bonsService: BonsService,
  ) {}

  ngOnInit(): void {
    this.authService.getUser().subscribe((result) => {
      this.merchantWalletAddress = result?.account ?? '';
      this.acceptedTicketsIds = Array.isArray(result?.tickets)
        ? result?.tickets
        : [];
      this.requestComensationForm
        .get('account')
        ?.setValue(this.merchantWalletAddress);

      this.ticketService.getAllTickets().subscribe((re) => {
        const rawAcceptedTickets = Array.isArray(re.tickets)
          ? re.tickets.filter((t) => this.acceptedTicketsIds.includes(t.id))
          : [];

        this.filterTicketsWithBalance(rawAcceptedTickets);
      });

      this.filterVouchersWithBalance();
    });

    this.ticketChangesSubscription = this.requestComensationForm
      .get('ticket')
      ?.valueChanges.subscribe((selectedTicketId) => {
        this.handleTicketChange(selectedTicketId);
      });
  }

  /**
   * Ne garde dans la liste déroulante que les titres B2B/B2C pour lesquels
   * le marchand a un solde disponible (> 0).
   */
  private filterTicketsWithBalance(tickets: any[]): void {
    if (!tickets.length || !this.merchantWalletAddress) {
      this.acceptedTickets = [];
      this.buildTicketOptions();
      return;
    }

    forkJoin(
      tickets.map((ticket) =>
        this.soldeService
          .getBalance(ticket.id, this.merchantWalletAddress)
          .pipe(
            map((response) => ({
              ticket,
              balance: response?.result?.balance || 0,
            })),
          ),
      ),
    ).subscribe(
      (results) => {
        this.acceptedTickets = results
          .filter((r) => r.balance > 0)
          .map((r) => r.ticket);
        this.buildTicketOptions();
      },
      (error) => {
        console.error('Failed to fetch ticket balances:', error);
        this.acceptedTickets = [];
        this.buildTicketOptions();
      },
    );
  }

  /**
   * Charge les bons cadeaux (dénominations ACTIVE) du marchand et leur solde
   * actuel — un tokenId par ligne, avec sa valeur totale détenue
   * (quantity × faceValue). Seuls ceux avec un solde > 0 sont affichés.
   */
  private filterVouchersWithBalance(): void {
    if (!this.merchantWalletAddress) {
      this.merchantVouchers = [];
      this.buildTicketOptions();
      return;
    }

    this.bonsService.getGroups().subscribe({
      next: (res: any) => {
        // Réponse groupée : chaque groupe porte plusieurs dénominations,
        // chacune avec son propre tokenId — on aplatit pour retrouver une
        // liste "un tokenId = une ligne".
        const denominations = (res.groups || []).flatMap((g: any) =>
          (g.denominations || [])
            .filter((d: any) => d.status === 'ACTIVE' && d.tokenId)
            .map((d: any) => ({ tokenId: d.tokenId, label: `${g.label} — ${d.faceValue} XOF` })),
        );

        if (!denominations.length) {
          this.merchantVouchers = [];
          this.buildTicketOptions();
          return;
        }

        forkJoin(
          denominations.map(
            (d: any): Observable<MerchantVoucher> =>
              this.soldeService.getVoucherBalance(d.tokenId, this.merchantWalletAddress).pipe(
                map((r: any) => ({
                  tokenId: d.tokenId as string,
                  label: d.label as string,
                  balance: Number(r?.result?.totalValue) || 0,
                })),
              ),
          ),
        ).subscribe({
          next: (results: unknown) => {
            const list = results as MerchantVoucher[];
            this.merchantVouchers = list.filter((r) => r.balance > 0);
            // Un bon disparu du solde (déjà dépensé/compensé entre deux
            // ouvertures de la modale) ne doit pas rester coché.
            this.selectedVoucherTokenIds = new Set(
              [...this.selectedVoucherTokenIds].filter((id) =>
                this.merchantVouchers.some((v) => v.tokenId === id),
              ),
            );
            this.buildTicketOptions();
          },
          error: (error) => {
            console.error('Failed to fetch voucher balances:', error);
            this.merchantVouchers = [];
            this.buildTicketOptions();
          },
        });
      },
      error: (error) => {
        console.error('Failed to fetch merchant vouchers:', error);
        this.merchantVouchers = [];
        this.buildTicketOptions();
      },
    });
  }

  private buildTicketOptions(): void {
    // "Bon marchand" est une option de plus dans le même menu déroulant que
    // les titres B2B/B2C classiques — la sélectionner révèle les cases à
    // cocher en dessous (voir handleTicketChange et le template).
    this.newRequestCompensationFields = [
      {
        controlName: 'ticket',
        label: 'Quel titre ?',
        type: 'select',
        placeholder: 'Sélectionner le type',
        required: false,
        options: [
          ...this.acceptedTickets.map((ticket) => ({
            id: ticket.id,
            label: ticket.label,
          })),
          ...(this.merchantVouchers.length
            ? [{ id: this.BON_MARCHAND_OPTION, label: 'Bon marchand' }]
            : []),
        ],
      },
      {
        controlName: 'amount',
        label: 'Montant B2B (optionnel)',
        type: 'number',
        placeholder: 'Entrer le montant',
        required: false,
      },
    ];
  }

  ngAfterViewInit(): void {
    if (typeof initFlowbite === 'function') {
      initFlowbite();
    } else {
      console.error('initFlowbite function not found.');
    }
  }

  ngOnDestroy(): void {
    this.ticketChangesSubscription?.unsubscribe();
    this.balanceSubscription?.unsubscribe();
  }

  handleTicketChange(selectedTicketId: any): void {
    this.balanceSubscription?.unsubscribe();
    this.currentBalance = 0;
    this.requestComensationForm.get('amount')?.setValue(null);

    if (!selectedTicketId || !this.merchantWalletAddress) {
      this.requestComensationForm
        .get('amount')
        ?.setValidators([Validators.min(0)]);
      this.requestComensationForm.get('amount')?.updateValueAndValidity();
      return;
    }

    // "Bon marchand" est une option sentinelle : elle ne correspond à aucun
    // titre B2B réel, elle sert juste à révéler les cases à cocher — aucun
    // solde à aller chercher ici.
    if (selectedTicketId === this.BON_MARCHAND_OPTION) {
      this.requestComensationForm.get('amount')?.setValidators([Validators.min(0)]);
      this.requestComensationForm.get('amount')?.updateValueAndValidity();
      return;
    }

    this.balanceSubscription = this.soldeService
      .getBalance(selectedTicketId, this.merchantWalletAddress)
      .pipe(map((response) => response?.result?.balance || 0))
      .subscribe(
        (balance) => {
          this.currentBalance = balance;
          this.requestComensationForm
            .get('amount')
            ?.setValidators([Validators.min(0), this.maxBalanceValidator(balance)]);
          this.requestComensationForm.get('amount')?.updateValueAndValidity();
        },
        (error) => {
          console.error('Failed to fetch balance:', error);
        },
      );
  }

  maxBalanceValidator(maxValue: number) {
    return (control: any) => {
      const value = Number(control.value);
      if (isNaN(value) || control.value === null) {
        return null;
      }
      return value > maxValue ? { maxBalance: { value, maxValue } } : null;
    };
  }

  // --- Cases à cocher "Bons cadeaux disponibles" ---

  isVoucherSelected(tokenId: string): boolean {
    return this.selectedVoucherTokenIds.has(tokenId);
  }

  toggleVoucher(tokenId: string, checked: boolean): void {
    if (checked) this.selectedVoucherTokenIds.add(tokenId);
    else this.selectedVoucherTokenIds.delete(tokenId);
  }

  /** Somme du montant B2B saisi + la valeur totale de chaque bon coché. */
  get totalDemande(): number {
    const b2bAmount = Number(this.requestComensationForm.get('amount')?.value) || 0;
    const vouchersTotal = this.merchantVouchers
      .filter((v) => this.selectedVoucherTokenIds.has(v.tokenId))
      .reduce((sum, v) => sum + v.balance, 0);
    return b2bAmount + vouchersTotal;
  }

  requestComensationForm = new FormGroup({
    account: new FormControl(this.merchantWalletAddress, [Validators.required]),
    ticket: new FormControl<string | number | null>(null),
    amount: new FormControl<number | null>(null, [Validators.min(0)]),
  });

  /**
   * Envoie une demande de compensation par titre concerné : une pour le
   * titre B2B sélectionné (si un montant a été saisi), une par bon coché —
   * toujours pour sa valeur totale. back-wallet convertit chaque montant de
   * bon en quantité d'unités à sa charge (voir
   * MerchantService.requestCompensation) ; le front n'envoie que des XOF.
   */
  onSubmit(): void {
    const account = this.merchantWalletAddress;
    const ticketId = this.requestComensationForm.get('ticket')?.value ?? null;
    const b2bAmount = Number(this.requestComensationForm.get('amount')?.value) || 0;
    const selectedVouchers = this.merchantVouchers.filter((v) =>
      this.selectedVoucherTokenIds.has(v.tokenId),
    );

       const requests: { ticketId: string | number; amount: number }[] = [];
    if (ticketId && ticketId !== this.BON_MARCHAND_OPTION && b2bAmount > 0) {
      requests.push({ ticketId, amount: b2bAmount });
    }
    for (const v of selectedVouchers) {
      requests.push({ ticketId: v.tokenId, amount: v.balance });
    }

    if (!requests.length) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Sélectionnez un titre B2B avec un montant, ou au moins un bon cadeau.',
      });
      return;
    }

    forkJoin(
      requests.map((r) =>
        this.benefitsService.requestCompensation(new TicketRequest(account, String(r.ticketId), r.amount)).pipe(          map(() => ({ success: true as const })),
          catchError((error) => {
            const backendMessage = error?.error?.result?.message || error?.error?.message;
            return of({ success: false as const, message: backendMessage });
          }),
        ),
      ),
    ).subscribe((results) => {
      const succeeded = results.filter((r) => r.success).length;
      const failed = results.length - succeeded;

      if (failed === 0) {
        this.show();
      } else if (succeeded > 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Partiellement envoyé',
          detail: `${succeeded} demande(s) envoyée(s), ${failed} en échec.`,
        });
      } else {
        const firstError = results.find((r) => !r.success) as { success: false; message?: string } | undefined;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            firstError?.message ||
            'Une erreur est survenue lors de votre demande de compensation, veuillez réessayer.',
        });
      }

      this.requestComensationForm.reset({
        account: this.merchantWalletAddress,
        ticket: null,
        amount: null,
      });
      this.selectedVoucherTokenIds = new Set();
      this.closeModal();
    });
  }

  show() {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Demande de compensation envoyée !',
    });
  }

  private getModalInstance(): ModalInterface | null {
    const modalElement = document.getElementById(this.modalId);
    if (modalElement) {
      return new Modal(modalElement);
    }
    console.warn(`Modal element with ID '${this.modalId}' not found.`);
    return null;
  }

  closeModal(): void {
    const modal = this.getModalInstance();
    if (modal) {
      modal.hide();
      const backdrop = document.querySelector('[modal-backdrop]');
      backdrop?.remove();
      document.body.classList.remove('overflow-hidden');
    }
  }

  openModal(): void {
    const modal = this.getModalInstance();
    if (modal) {
      modal.show();
    }
  }
}